import React, { useEffect, useRef, useState } from 'react';
import { ANIMAL_STRENGTH, ANIMAL_LABEL } from '../common/oasisCatalog.js';
import CardBackDesign from './CardBackDesign.js';

/**
 * Renders a single animal card. Face-down cards show a plain card-back,
 * except the viewer's own face-down cards, which reveal with reduced
 * opacity -- matching the physical game where you can always peek at your
 * own hidden card. On a real hover-capable pointer this follows the mouse
 * (hover to reveal, leave to hide again); on touch there's no such thing
 * as "still hovering", so a tap reveals it for 3 seconds (or until tapped
 * again) instead -- and, critically, hover is never wired up on touch at
 * all, since iOS Safari's touch-to-mouse-event emulation treats the first
 * tap on anything with a mouseenter handler as only entering hover state,
 * requiring a 2nd tap to actually register as a click.
 */
export default function AnimalCard({
  card,
  mine,
  selected,
  playable,
  clickable,
  pending,
  highlight,
  pullCandidate,
  pullChosen,
  dimmed,
  pendingRemoval,
  effectiveStrength,
  flipping,
  onClick,
  onHoverStart,
  onHoverEnd,
}) {
  const [peeking, setPeeking] = useState(false);
  const peekTimerRef = useRef(null);
  const [supportsHover] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
  );

  const isFaceDown = card.face === 'down';
  const canPeek = isFaceDown && mine;
  const showFace = !isFaceDown || (canPeek && peeking);

  useEffect(() => () => clearTimeout(peekTimerRef.current), []);

  function handleClick(e) {
    if (canPeek) {
      clearTimeout(peekTimerRef.current);
      if (peeking) {
        setPeeking(false);
      } else {
        setPeeking(true);
        peekTimerRef.current = setTimeout(() => setPeeking(false), 3000);
      }
      // Don't also treat a peek-tap as a card selection tap.
      if (!onClick) return;
    }
    onClick?.(e);
  }

  const classNames = ['animal-card'];
  if (!flipping && showFace) classNames.push(`animal-card-${card.animal}`);
  if (selected) classNames.push('animal-card-selected');
  if (playable) classNames.push('animal-card-playable');
  if (playable || clickable || canPeek) classNames.push('animal-card-interactive');
  if (isFaceDown && !showFace) classNames.push('animal-card-facedown');
  if (canPeek && peeking) classNames.push('animal-card-peeking');
  if (pending) classNames.push('animal-card-pending');
  if (highlight) classNames.push('animal-card-highlight');
  if (pullCandidate) classNames.push('animal-card-pull-candidate');
  if (pullCandidate && pullChosen) classNames.push('animal-card-pull-chosen');
  if (dimmed) classNames.push('animal-card-dimmed');
  if (pendingRemoval) classNames.push('animal-card-pending-removal');
  if (flipping) classNames.push('animal-card-flip-scene');

  const faceContent = (
    <>
      <div className="animal-card-strength animal-card-strength-tl">
        {card.animal === 'chameleon' ? effectiveStrength ?? '?' : ANIMAL_STRENGTH[card.animal]}
      </div>
      <div className="animal-card-strength animal-card-strength-br">
        {card.animal === 'chameleon' ? effectiveStrength ?? '?' : ANIMAL_STRENGTH[card.animal]}
      </div>
      {card.animal === 'chameleon' && card.orientation && (
        <div className={`animal-card-orientation animal-card-orientation-${card.orientation}`}>
          {card.orientation === 'left' ? '←' : '→'}
        </div>
      )}
      <div className="animal-card-center">
        <div className="animal-card-label">{ANIMAL_LABEL[card.animal]}</div>
      </div>
    </>
  );

  return (
    <div
      className={classNames.join(' ')}
      title={pendingRemoval ? "Will be removed by this scorpion's power once submitted" : pending ? 'Not yet submitted' : undefined}
      onClick={handleClick}
      onMouseEnter={() => {
        if (canPeek && supportsHover) {
          clearTimeout(peekTimerRef.current);
          setPeeking(true);
        }
        onHoverStart?.();
      }}
      onMouseLeave={() => {
        if (canPeek && supportsHover) setPeeking(false);
        onHoverEnd?.();
      }}
    >
      {flipping ? (
        // A real double-sided 3D flip: both faces are rendered at once,
        // each hidden on its own backside (backface-visibility), rotated
        // 180deg apart -- the container rotating from 0->180deg naturally
        // shows the card-back first and the true face only once it's
        // rotated past the edge-on midpoint, instead of an instant swap.
        <div className="animal-card-flip-inner">
          <div className="animal-card-flip-face animal-card-flip-face-back">
            <div className="animal-card-back">
              <CardBackDesign />
            </div>
          </div>
          <div className={`animal-card-flip-face animal-card-flip-face-front animal-card-${card.animal}`}>
            {faceContent}
          </div>
        </div>
      ) : showFace ? (
        faceContent
      ) : (
        <div className="animal-card-back">
          <CardBackDesign />
        </div>
      )}
      {pendingRemoval && (
        <div className="animal-card-removal-x" aria-hidden="true">
          ✕
        </div>
      )}
    </div>
  );
}
